/**
 * Edit reconciliation for the reference text input.
 *
 * A native `<input>` only ever reports a new value string per change event, so
 * we recover the effect of a free-text edit by diffing the previous and next
 * display strings (a single contiguous change region) and re-projecting the
 * surviving mention ranges. Mentions the edit cut into are demoted to plain
 * text. Whole-token deletion is handled separately by `computeAtomicDelete`,
 * which the component invokes on keydown before the browser mutates the value.
 */

import { MentionRange, ParsedValue } from './serialization';

/**
 * Reconcile a free-text edit. `nextDisplay` is the input's new value; the
 * returned mentions are those from `prevMentions` that the edit left intact,
 * re-positioned into `nextDisplay` coordinates.
 */
export function reconcile(
  prevDisplay: string,
  prevMentions: MentionRange[],
  nextDisplay: string
): ParsedValue {
  // Longest common prefix.
  let prefix = 0;
  const maxPrefix = Math.min(prevDisplay.length, nextDisplay.length);
  while (prefix < maxPrefix && prevDisplay[prefix] === nextDisplay[prefix]) {
    prefix += 1;
  }

  // Longest common suffix that doesn't overlap the prefix.
  let suffix = 0;
  const maxSuffix = Math.min(
    prevDisplay.length - prefix,
    nextDisplay.length - prefix
  );
  while (
    suffix < maxSuffix &&
    prevDisplay[prevDisplay.length - 1 - suffix] ===
      nextDisplay[nextDisplay.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  const changeStart = prefix;
  const prevChangeEnd = prevDisplay.length - suffix; // exclusive, prev coords
  const delta = nextDisplay.length - prevDisplay.length;

  const mentions: MentionRange[] = [];
  for (const mention of prevMentions) {
    if (mention.end <= changeStart) {
      // Entirely before the edit — unaffected.
      mentions.push({ ...mention });
    } else if (mention.start >= prevChangeEnd) {
      // Entirely after the edit — shift by the length delta.
      mentions.push({
        ...mention,
        start: mention.start + delta,
        end: mention.end + delta,
      });
    }
    // Otherwise the edit intruded into the mention — drop it (demote to text).
  }

  return { display: nextDisplay, mentions };
}

export interface DeleteResult extends ParsedValue {
  /** Caret position (display coords) to restore after the deletion. */
  caret: number;
}

/**
 * Compute a whole-token ("atomic") deletion for a Backspace/Delete keystroke.
 *
 * Returns `null` when the keystroke does not touch a mention, signalling the
 * component to let the browser perform its normal character deletion (which the
 * subsequent change event reconciles). Otherwise returns the display + mentions
 * after removing the whole token(s) and the caret position to restore.
 */
export function computeAtomicDelete(
  display: string,
  mentions: MentionRange[],
  selectionStart: number,
  selectionEnd: number,
  direction: 'backward' | 'forward'
): DeleteResult | null {
  if (selectionStart !== selectionEnd) {
    // Range selection: grow it to fully cover any partially-selected mention,
    // then delete the expanded range. If no mention is partially cut, defer to
    // the native delete.
    let from = selectionStart;
    let to = selectionEnd;
    let cutsMention = false;
    for (const mention of mentions) {
      const overlaps = mention.start < to && mention.end > from;
      if (overlaps) {
        if (mention.start < from) {
          from = mention.start;
          cutsMention = true;
        }
        if (mention.end > to) {
          to = mention.end;
          cutsMention = true;
        }
      }
    }
    if (!cutsMention) return null;
    return removeRange(display, mentions, from, to);
  }

  const caret = selectionStart;

  // Backspacing the plain space immediately after a mention removes both the
  // space and the whole token (the space auto-inserted on selection, or any
  // single space typed directly after a token).
  if (direction === 'backward' && caret > 0 && display[caret - 1] === ' ') {
    const tokenBeforeSpace = mentions.find((m) => m.end === caret - 1);
    if (tokenBeforeSpace) {
      return removeRange(display, mentions, tokenBeforeSpace.start, caret);
    }
  }

  const target =
    direction === 'backward'
      ? mentions.find((m) => m.start < caret && caret <= m.end)
      : mentions.find((m) => m.start <= caret && caret < m.end);

  if (!target) return null;
  return removeRange(display, mentions, target.start, target.end);
}

function removeRange(
  display: string,
  mentions: MentionRange[],
  from: number,
  to: number
): DeleteResult {
  const removedLength = to - from;
  const nextDisplay = display.slice(0, from) + display.slice(to);
  const nextMentions = mentions
    // Drop any mention that lies within the removed range.
    .filter((m) => m.end <= from || m.start >= to)
    // Shift mentions that came after the removed range.
    .map((m) =>
      m.start >= to
        ? { ...m, start: m.start - removedLength, end: m.end - removedLength }
        : { ...m }
    );
  return { display: nextDisplay, mentions: nextMentions, caret: from };
}
